import { StatsigClient } from '@statsig/js-client';
import datadogLoggingServiceInstance from "@/revamp/utils/datadogLoggingService";

class FeatureFlagService {
  constructor() {
    this.client = null;
    this.isInitialized = false;
    this.isLoading = false;
    this.error = null;
    this.initializationPromise = null;
  }

  // Initialize Statsig client
  async initialize(userId = null) {
    // If already initialized, return immediately
    if (this.isInitialized) {
      console.log("Already Initialized");
      return;
    }

    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    // Start initialization
    this.initializationPromise = this._performInitialization(userId);
    return this.initializationPromise;
  }

  async _performInitialization(userId) {
    try {
      this.isLoading = true;
      this.error = null;

      // Get SDK key from environment
      const sdkKey = process.env.VUE_APP_STATSIG_API_KEY;
      if (!sdkKey) {
        throw new Error('VUE_APP_STATSIG_API_KEY not found in environment variables');
      }

      // Create user object
      const user = {
        userID: userId || 'anonymous_user',
      };

      // Client options
      const options = {
        plugins: [],
        environment: {
          tier: process.env.NODE_ENV != "production" ? "development" : "production"
        }
      };

      // Initialize client
      this.client = new StatsigClient(sdkKey, user, options);
      await this.client.initializeAsync();

      this.isInitialized = true;
      datadogLoggingServiceInstance.logInfo('Statsig successfully initialized');
    } catch (err) {
      datadogLoggingServiceInstance.logError(`Failed to initialize Statsig: ${err.toString()}`);
      this.error = err.message;
      throw err;
    } finally {
      this.isLoading = false;
    }
  }

  // Check if a feature gate is enabled
  checkGate(gateName, defaultValue = false) {
    if (!this.isInitialized || !this.client) {
      datadogLoggingServiceInstance.logInfo(`Statsig not initialized. Gate "${gateName}" defaulting to ${defaultValue}`);
      return defaultValue;
    }

    try {
      return this.client.checkGate(gateName);
    } catch (err) {
      datadogLoggingServiceInstance.logError(`Error checking gate "${gateName}": ${err.toString()}`);
      return defaultValue;
    }
  }
}

// Create and export singleton instance
const useFeatureFlags = new FeatureFlagService();

export default useFeatureFlags;